import api from "@/lib/api";

const POST_EDGE = 1600;
const AVATAR_EDGE = 800;
const MAX_FILE = 25 * 1024 * 1024;

/** Shrink a photo on the phone before it is sent: a 12-megapixel photo becomes a few hundred KB, which is far quicker on mobile data.
 *  If the browser can't read the file (HEIC, GIF...) the original is sent and the server decides. */
export async function shrink(file, maxEdge = POST_EDGE) {
  try {
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || typeof createImageBitmap !== "function") return file;
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) { bmp.close?.(); return file; }
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0D0A0E"; ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    return blob || file;
  } catch {
    return file;
  }
}

/** Upload one photo. purpose is "post" or "avatar". Resolves to {id, url, thumb, w, h}; rejects with the server's own message. */
export async function uploadPhoto(file, purpose = "post") {
  if (file.size > MAX_FILE) throw { response: { data: { detail: "That photo is too large. Please choose one under 12 MB." } } };
  const body = await shrink(file, purpose === "avatar" ? AVATAR_EDGE : POST_EDGE);
  const r = await api.post(`/media?purpose=${purpose}`, body, {
    headers: { "Content-Type": body.type || file.type || "application/octet-stream" },
    transformRequest: [(d) => d],
  });
  return r.data;
}